import type { RowData } from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useI18n } from "@/locales/i18n-provider";
import type { DataTableInstance } from "./use-data-table";

type DataTableColumnHeaderProps<TData extends RowData> = {
  children: ReactNode;
  column: ReturnType<DataTableInstance<TData>["getAllLeafColumns"]>[number];
};

/** HRMS 的紧凑表头和排序菜单，使用项目现有的 Radix 控件。 */
export function DataTableColumnHeader<TData extends RowData>({ children, column }: DataTableColumnHeaderProps<TData>) {
  const { t } = useI18n();
  const sorted = column.getIsSorted();
  const SortIcon = sorted === "asc" ? ArrowUpIcon : sorted === "desc" ? ArrowDownIcon : ArrowUpDownIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="data-table-column-trigger -ms-1 h-5 gap-1 px-1" size="sm" variant="ghost">
          {children}
          <SortIcon aria-hidden="true" data-icon="inline-end" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuGroup>
          <DropdownMenuItem onSelect={() => column.toggleSorting(false)}>
            <ArrowUpIcon />
            {t("dataTable.sort.ascending")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => column.toggleSorting(true)}>
            <ArrowDownIcon />
            {t("dataTable.sort.descending")}
          </DropdownMenuItem>
          {sorted ? <DropdownMenuItem onSelect={() => column.clearSorting()}>{t("dataTable.sort.clear")}</DropdownMenuItem> : null}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
